import React, { useEffect, useState } from 'react';
import { Alert, Button, Form, Spinner } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { BiArrowBack } from 'react-icons/bi';
import { platformAPI } from '../../../services/platformAPI';
import WorkspacePageHeader from '../../../components/workspace/WorkspacePageHeader';
import { useAdminRecordDelete } from '../../../components/workspace/useAdminRecordDelete';
import { useMasterRevision } from '../../../components/workspace/useMasterRevision';
import useDocumentTitle from '../../../hooks/useDocumentTitle';
import './CustomerMaster.scss';

const blankItem = () => ({ code: '', label: '', required: true, guidance: '' });

const DiligenceTemplatesPage = () => {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  useDocumentTitle('Due-diligence checklists');

  const load = () => {
    setLoading(true);
    platformAPI.listDiligenceTemplates()
      .then((response) => {
        const rows = response.data?.data?.templates || [];
        setTemplates(rows);
        setSelected((current) => current || (rows[0] ? { ...rows[0], items: rows[0].items || [] } : null));
      })
      .catch((err) => setError(err.response?.data?.message || 'Checklists could not be loaded'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const admin = useAdminRecordDelete({
    entity: 'diligence',
    noun: 'checklist',
    plural: 'checklists',
    reload: load,
    label: (row) => row.name
  });
  const revision = useMasterRevision({ entity: 'diligence' });

  const updateItem = (index, key, value) => {
    setSelected((current) => {
      const items = current.items.map((item, itemIndex) => (itemIndex === index ? { ...item, [key]: value } : item));
      return { ...current, items };
    });
  };

  const save = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError('');
    setNotice('');
    try {
      const changeReason = await revision.askReason(selected.revision);
      if (!changeReason) return;
      const response = await platformAPI.saveDiligenceTemplate({
        id: selected._id,
        name: selected.name,
        participationType: selected.participationType || '',
        isActive: selected.isActive !== false,
        items: selected.items,
        changeReason
      });
      setNotice(response.data?.message || 'Checklist saved. Reviews already on a tender keep the version they used.');
      setSelected(response.data?.data?.template || selected);
      load();
    } catch (err) {
      setError(err.response?.data?.message || 'The checklist could not be saved');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="cust-page">
      <button type="button" className="cust-page__back" onClick={() => navigate('/apps/tender/masters')}>
        <BiArrowBack />
        Masters
      </button>
      <WorkspacePageHeader
        title="Due-diligence checklists"
        help="masters"
        subtitle="A review copies the template and version in use at that moment. Later edits do not change a review that is already on a tender."
      />
      {loading ? <Spinner animation="border" size="sm" /> : null}
      {error ? <Alert variant="danger">{error}</Alert> : null}
      {notice ? <Alert variant="info">{notice}</Alert> : null}
      {admin.notice}
      {selected ? (
        <form onSubmit={save}>
          {selected.isSample ? <Alert variant="info">This starting checklist is editable. It is not a confirmed firm policy. The review is manual and uses the evidence supplied on the tender.</Alert> : null}
          <div className="row g-2 mb-3">
            <div className="col-md-5">
              <Form.Label>Template</Form.Label>
              <Form.Select value={selected._id} onChange={(event) => setSelected(templates.find((row) => row._id === event.target.value))}>
                {templates.map((row) => <option key={row._id} value={row._id}>{row.name} · version {row.version} · revision {row.revision || 1}</option>)}
              </Form.Select>
            </div>
            <div className="col-md-3">
              <Form.Label>Name</Form.Label>
              <Form.Control value={selected.name || ''} onChange={(event) => setSelected({ ...selected, name: event.target.value })} required />
            </div>
            <div className="col-md-2">
              <Form.Label>Participation</Form.Label>
              <Form.Control value={selected.participationType || ''} placeholder="Any" onChange={(event) => setSelected({ ...selected, participationType: event.target.value })} />
            </div>
            <div className="col-md-2">
              <Form.Label>Status</Form.Label>
              <Form.Select value={selected.isActive === false ? 'inactive' : 'active'} onChange={(event) => setSelected({ ...selected, isActive: event.target.value === 'active' })}>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </Form.Select>
            </div>
          </div>
          {(selected.items || []).map((item, index) => (
            <div className="row g-2 mb-2" key={`${item.code}-${index}`}>
              <div className="col-md-4">
                <Form.Control aria-label="Checklist item" value={item.label} onChange={(event) => updateItem(index, 'label', event.target.value)} required />
              </div>
              <div className="col-md-5">
                <Form.Control aria-label="Guidance" value={item.guidance || ''} placeholder="Guidance" onChange={(event) => updateItem(index, 'guidance', event.target.value)} />
              </div>
              <div className="col-md-3">
                <Form.Select aria-label="Required or optional" value={item.required === false ? 'optional' : 'required'} onChange={(event) => updateItem(index, 'required', event.target.value === 'required')}>
                  <option value="required">Required</option>
                  <option value="optional">Optional</option>
                </Form.Select>
              </div>
            </div>
          ))}
          <div className="d-flex gap-2 mt-3">
            <Button type="button" size="sm" variant="outline-primary" onClick={() => setSelected({ ...selected, items: [...selected.items, blankItem()] })}>Add item</Button>
            <Button type="submit" size="sm" disabled={saving}>{saving ? 'Saving…' : 'Save checklist'}</Button>
            <Button type="button" size="sm" variant="outline-secondary" onClick={() => revision.open(selected)}>Revision info</Button>
            {admin.enabled ? (
              <>
                <Button type="button" size="sm" variant="outline-danger" onClick={() => admin.ask('one', [selected._id], [selected.name])}>Delete this checklist</Button>
                <Button type="button" size="sm" variant="outline-danger" onClick={() => admin.ask('all', templates.map((row) => row._id), templates.map((row) => row.name))}>Delete all</Button>
              </>
            ) : null}
          </div>
        </form>
      ) : null}
      {admin.modal}
      {revision.modals}
    </div>
  );
};

export default DiligenceTemplatesPage;
